import React, { useState, useEffect } from "react";
import "./App.css";
import MaintenancePage from "./components/MaintenancePage";
import profileOfflineIcon from "./icons/profileoffline.png";
import profileOnlineIcon from "./icons/profileonline.png";
import exitIcon from "./icons/exit.png";
import signInIcon from "./icons/sign-in.png";
import signUpIcon from "./icons/sign-up.png";
import ChooseYourShowContent from "./components/ChooseYourShowContent";
import YourSpaceContent from "./components/YourSpaceContent";
import TermsContent from "./components/TermsContent";
import PrivacyContent from "./components/PrivacyContent";
import AfterPayment from "./components/AfterPayment";
import YourTicketsContent from "./components/YourTicketsContent";
import FavShowsContent from "./components/FavShowsContent";
import SupportContent from "./components/SupportContent";
import UnderskinXIII from "./components/UnderskinXIII/UnderskinXIII.js";
import OutcastDomain from "./components/OutcastDomain/OutcastDomain.js";
import axios from "axios";

import bgMobile from "./bg/bg-mobile-opt.mp4";
import bgLandscape from "./bg/bg-landscape-opt.mp4";

const arrayBufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  let base64String = window.btoa(binary);
  base64String = base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return base64String;
};

const base64UrlToArrayBuffer = (base64urlString) => {
  let base64String = base64urlString.replace(/-/g, '+').replace(/_/g, '/');
  while (base64String.length % 4) {
    base64String += '=';
  }
  const binaryString = window.atob(base64String);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const isMaintenanceMode = true;
  const [isIpAllowed, setIsIpAllowed] = useState(false);

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const openAuthModal = () => setAuthModalOpen(true);
  const closeAuthModal = () => setAuthModalOpen(false);
  const isUnderskinPage = window.location.pathname.replace(/\/+$/, "") === "/underskin-xiii";
  const isOutcastPage = window.location.pathname.replace(/\/+$/, "") === "/outcast-domain";
  const isLegalStep = currentStep === 6 || currentStep === 7;

  useEffect(() => {
    const checkIpRoute = async () => {
      try {
        const response = await axios.get("/api/ip/check-ip");
        if (response.data && response.data.allowed === true) {
          setIsIpAllowed(true);
        } else {
          setIsIpAllowed(false);
        }
      } catch (error) {
        console.error("Error checking IP on server:", error);
        setIsIpAllowed(false);
      }
    };

    checkIpRoute();
  }, []);

  const isMaintenanceActive = isMaintenanceMode && !isIpAllowed;

  const onNavigateToStep = (step) => {
    setCurrentStep(step);
  };

  const generateUserId = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return array.buffer;
  };

  const setUserId = (userIdBase64Url) => {
      localStorage.setItem("userId", userIdBase64Url);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const interceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const tryAutoAuthenticate = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const response = await axios.get('/api/users/verify-token', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.data && response.data.valid) {
            setIsAuthenticated(true);
            localStorage.setItem('isLoggedIn', 'true');
            if (response.data.userId) {
              localStorage.setItem('userId', response.data.userId);
	      
              await createOrEnsureMailbox(response.data.userId);
            }
          } else {
            throw new Error('Invalid token');
          }
        } catch {
          localStorage.removeItem('authToken');
          localStorage.setItem('isLoggedIn', 'false');
        }
      } else if (localStorage.getItem('isLoggedIn') === 'true') {
        if (window.PublicKeyCredential) {
          tryAutoAuthenticate();
        }
      }
    };

    tryAutoAuthenticate();
  }, []);

  const createOrEnsureMailbox = async (maybeUserId) => {
    try {
      const userIdFromStorage = maybeUserId || localStorage.getItem('userId');
      if (!userIdFromStorage) return;
      const response = await axios.post('/api/mail/assign', { userId: userIdFromStorage });
      if (response.data.success) {
        if (response.data.alreadyExisted) {
        } else {
        }
      } else {
      }
    } catch (err) {
      console.error("Error ensuring mailbox:", err);
    }
  };

  const handleAuthentication = async () => {
    setShowSpinner(true);
    try {
      const authenticationOptionsResponse = await axios.post(
        "/api/users/generate-authentication-options"
      );
      const options = authenticationOptionsResponse.data;

      options.challenge = base64UrlToArrayBuffer(options.challenge);

      if (options.allowCredentials && options.allowCredentials.length > 0) {
        options.allowCredentials = options.allowCredentials.map((cred) => ({
          ...cred,
          id: base64UrlToArrayBuffer(cred.id),
        }));
      } else {
        delete options.allowCredentials;
      }

      const assertion = await navigator.credentials.get({ publicKey: options });
      const assertionData = {
        id: assertion.id,
        rawId: arrayBufferToBase64Url(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: arrayBufferToBase64Url(assertion.response.authenticatorData),
          clientDataJSON: arrayBufferToBase64Url(assertion.response.clientDataJSON),
          signature: arrayBufferToBase64Url(assertion.response.signature),
          userHandle: assertion.response.userHandle
            ? arrayBufferToBase64Url(assertion.response.userHandle)
            : null,
        },
        clientExtensionResults: assertion.getClientExtensionResults(),
      };

      const verificationResponse = await axios.post(
        "/api/users/verify-authentication",
        assertionData
      );

      if (verificationResponse.data && verificationResponse.data.success) {
        setIsAuthenticated(true);
	localStorage.setItem('isLoggedIn', 'true');
	localStorage.setItem('authToken', verificationResponse.data.token);
	if (verificationResponse.data.userId) {
        localStorage.setItem('userId', verificationResponse.data.userId);

        await createOrEnsureMailbox(verificationResponse.data.userId);
      }

      } else {
        await handleRegistration();
      }
    } catch {
      await handleRegistration();
    } finally {
    setShowSpinner(false);
   }
  };

  const handleRegistration = async () => {
    setShowSpinner(true);
    try {
      const userIdBuffer = generateUserId();
      const userIdBase64Url = arrayBufferToBase64Url(userIdBuffer);
      const username = "cultuser";
      const displayName = "Cult Prjct Platform";

      const registrationOptionsResponse = await axios.post(
        "/api/users/generate-registration-options",
        {
          userId: userIdBase64Url,
          username,
          displayName,
        }
      );

      const options = registrationOptionsResponse.data;

      options.challenge = base64UrlToArrayBuffer(options.challenge);
      options.user.id = base64UrlToArrayBuffer(options.user.id);

      if (options.excludeCredentials && options.excludeCredentials.length > 0) {
        options.excludeCredentials = options.excludeCredentials.map((cred) => ({
          ...cred,
          id: base64UrlToArrayBuffer(cred.id),
        }));
      }

      const credential = await navigator.credentials.create({ publicKey: options });
      const clientDataJSON = arrayBufferToBase64Url(credential.response.clientDataJSON);
      const attestationObject = arrayBufferToBase64Url(credential.response.attestationObject);

      const credentialData = {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON,
          attestationObject,
        },
        clientExtensionResults: credential.getClientExtensionResults(),
      };

      const verificationResponse = await axios.post("/api/users/verify-registration", {
        credential: credentialData,
        userId: userIdBase64Url,
      });

      if (verificationResponse.data && verificationResponse.data.success) {
        setUserId(userIdBase64Url);
        setIsAuthenticated(true);
	localStorage.setItem('isLoggedIn', 'true');
	localStorage.setItem('authToken', verificationResponse.data.token);

	await createOrEnsureMailbox(userIdBase64Url);
      }
    } catch {}
      finally {
        setShowSpinner(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("userId");
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('authToken');
  };
  
  const getQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      concertId: params.get('concertId'),
      orderId: params.get('orderId'),
    };
  };

  useEffect(() => {
    const { concertId, orderId } = getQueryParams();

    if (concertId) {
      setCurrentStep(1);
    }
    if (orderId) {
      setCurrentStep(8);
      fetchPaymentStatus(orderId);
    }
  }, []);

  const fetchPaymentStatus = async (orderId) => {
    try {
      const response = await axios.get(`/api/payments/status?orderId=${encodeURIComponent(orderId)}`);
      setPaymentStatus(response.data.status);
    } catch {
      setPaymentStatus('error');
    }
  };

  const sendEvent = async (eventName, customData = {}) => {
  try {
    const response = await fetch('/api/track-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventName,
        customData,
      }),
    });

    const data = await response.json();
    if (data.success) {
    } else {
      console.error(`Помилка при відправці події "${eventName}":`, data.error);
    }
  } catch (error) {
    console.error(`Помилка при відправці події "${eventName}":`, error);
  }
};

  const steps = [
    {
      id: "welcome",
      content: (
        <div className="content visible">
          <img src="/logo.svg" className="App-logo" alt="logo" />
          <div className="button-container">
            <button className="button" onClick={() => setCurrentStep(1)}>
              <div className="button-content">
                <span className="button-text">ОБРАТИ ПОДІЮ</span>
              </div>
            </button>
            <button className="button" onClick={() => setCurrentStep(2)}>
              <div className="button-content">
                <span className="button-text">ТВІЙ ПРОСТІР</span>
              </div>
            </button>
          </div>
        </div>
      ),
    },
    {

  id: "choose-show",

  content: (

    <ChooseYourShowContent

      onBack={() => setCurrentStep(0)}

      isAuthenticated={isAuthenticated}

      sendEvent={sendEvent}

      onOpenAuthModal={openAuthModal}

    />

  ),

},
    {
      id: "your-space",
      content: (
        <YourSpaceContent
          onBack={() => onNavigateToStep(0)}
          onNavigateToStep={onNavigateToStep}
        />
      ),
    },
    {
      id: "your-tickets",
      content: (
        <YourTicketsContent
          onBack={() => onNavigateToStep(2)}
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
        />
      ),
    },
    {
      id: "fav-shows",
      content: <FavShowsContent onBack={() => onNavigateToStep(2)} isAuthenticated={isAuthenticated} sendEvent={sendEvent} />,
    },
    {
      id: "your-support",
      content: <SupportContent onBack={() => onNavigateToStep(2)} isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
    },
    {
      id: "terms",
      content: <TermsContent onBack={() => setCurrentStep(0)} />,
    },
    {
      id: "privacy",
      content: <PrivacyContent onBack={() => setCurrentStep(0)} />,
    },
    {
      id: "after-payment",
      content: <AfterPayment status={paymentStatus} onBack={() => setCurrentStep(3)} />,
    },
  ];

  const renderStep = () => {
    return steps[currentStep].content;
  };

   return isMaintenanceActive ? (
    <MaintenancePage />
  ) : (
    <>
    <video
      className="bg-video mobile-bg"
      src={bgMobile}
      autoPlay
      loop
      muted
      playsInline
    />
    <video
      className="bg-video landscape-bg"
      src={bgLandscape}
      autoPlay
      loop
      muted
      playsInline
    />
    <div className={`App ${[1, 2, 3, 4, 5].includes(currentStep) ? "events-list-page" : ""}`}>
      <header className="App-header">
        <div className="authButtonContainer">
          <button
	  className="authButton"
	  onClick={isAuthenticated ? handleLogout : openAuthModal}
	  aria-label="Indicator"
	>
	  <img
	    src={isAuthenticated ? profileOnlineIcon : profileOfflineIcon}
	    alt={isAuthenticated ? "Online" : "Offline"}
	    className="profileIcon"
	  />
	</button>

	  {!isAuthenticated && currentStep === 0 && (
	  <span className="topLabel">Індикатор</span>
	  )}
        </div>

      {/*  {!isAuthenticated && currentStep === 0 && (
          <div className="signInUpContainer">
	   <span className="topLabel">Увійти / Реєстрація</span>
            <button
              className="signIconButton"
              onClick={handleAuthentication}
              aria-label="Sign in"
            >
              <img src={signInIcon} alt="" className="signIcon" />
            </button>

            <button
              className="signIconButton"
              onClick={handleRegistration}
              aria-label="Sign up"
            >
              <img src={signUpIcon} alt="" className="signIcon" />
            </button>
          </div>
        )} */}

        {isAuthenticated && currentStep === 0 && (
          <div className="exitContainer">
            <button className="exitButton" onClick={handleLogout}>
              <img src={exitIcon} alt="Exit" className="exitIcon" />
            </button>
          </div>
        )}
        {(!isLegalStep && isUnderskinPage) ? (
	  <UnderskinXIII sendEvent={sendEvent} />
	) : (!isLegalStep && isOutcastPage) ? (
	  <OutcastDomain sendEvent={sendEvent} />
	) : (
	  renderStep()
	)}
      </header>

      <footer className="footer">
        <div className="footer-content slide-up">
          <button className="footer-link" onClick={() => setCurrentStep(7)}>
            Конфіденційність
          </button>
          <span className="footer-divider">|</span>
          <button className="footer-link" onClick={() => setCurrentStep(6)}>
            Умови
          </button>
          <span className="footer-divider">|</span>
          <span className="footer-copy">© Cult Project 2026</span>
        </div>
      </footer>

     {authModalOpen && (
	  <div className="authModalOverlay" onClick={closeAuthModal}>
	    <div className="authModal" onClick={(e) => e.stopPropagation()}>
	      <button className="authModalClose" onClick={closeAuthModal} aria-label="Close">
                ×
              </button>

	      <div className="authModalTitle">Мій Профіль</div>

	      <button
		className="authModalBtn"
		onClick={async () => {
		  closeAuthModal();
		  await handleAuthentication();
		}}
	      >
		Увійти
	      </button>

	      <button
		className="authModalBtn"
		onClick={async () => {
		  closeAuthModal();
		  await handleRegistration();
		}}
	      >
		Реєстрація
	      </button>
	    </div>
	  </div>
	)}
     {showSpinner && (
          <div className="spinnerOverlay">
            <div className="spinnerLoader"></div>
          </div>
        )}
    </div>
   </>
  );
}

export default App;

